import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNil, trim } from 'lodash';
import FlipMove from 'react-flip-move';
import linkifyIt from 'linkify-it';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import ATbutton from '@at.govt.nz/buttons';
import { ReactComponent as IconAlert } from '@at.govt.nz/vector/lib/img/icon-warning.svg';
import MovementInfo from './components/MovementInfo';
import LiveDepartureActions from '../../redux/LiveDeparture';
import {
  displaySurveyButtonSelector,
  filteredMovementsSelector,
  getSelectedStop,
  getMessage,
  getHasEmergencyMessage,
  getHasCriticalMessage,
  getMovements,
  hasErrorSelector,
  hasFilteredMovementsSelector,
  hasMovementsSelector,
  hasNoDataSelector,
  hasNoMoreMovements,
  isRequestingMoreMovements,
} from '../../selectors/DepartureBoardSelectors';
import { CATEGORIES, LIVE_DEPARTURES_ACTION, logEvent } from '../../services/AnalyticsService';
import { getStopByCode, getStopById } from '../../services/GtfsService';
import { stopToRouteMode } from '../../services/LiveDepartureService';
import FilterRouteOptionsDialog from './FilterRouteOptionsDialog';
import SurveyButton from '../../components/SurveyButton/SurveyButton';
import { ReactComponent as IconOptions } from '../../assets/svg/icon-options-white.svg';

const SURVEY_URL = process.env.SURVEY_LIVE_DEPARTURE_URL || 'https://aucklandtransport.au1.qualtrics.com/jfe/form/SV_2s4IriwHJMik2Lb';
const DATA_FETCH_INTERVAL = 20 * 1000;
const LOADING_SPINNER_HEIGHT = 50;
const linkify = linkifyIt();

const Spinner = forwardRef((props, ref) => (
  <li ref={ref} className="loading-wrapper" key="spinner">
    <div className="loading-spinner-small" />
  </li>
));

const LiveDepartureLoading = () => (
  <div className="stop-message">
    <div className="loader stop-message__loader" />
  </div>
);

const LiveDepartureError = () => (
  <div className="stop-message">
    <h2 className="stop-message__title">Data temporarily unavailable</h2>
    <p className="stop-message__description">We will update this stop when the issue has been resolved</p>
  </div>
);

const LiveDepartureNoResult = () => (
  <div className="stop-message">
    <h2 className="stop-message__title">No services in the next 2 hours</h2>
    <p className="stop-message__description">To find an alternate route, use the Journey planner</p>
  </div>
);

const LiveDepartureFiltered = ({ mode }) => {
  const title = `No services for selected ${mode.toLowerCase()} routes in the next 2 hours`;
  return (
    <div className="stop-message">
      <h2 className="stop-message__title">{title}</h2>
      <p className="stop-message__description">To find an alternate route, use the Journey planner</p>
    </div>
  );
};
LiveDepartureFiltered.propTypes = {
  mode: PropTypes.string.isRequired,
};

const LiveDepartureList = ({ stop }) => {
  const filteredMovements = useSelector(filteredMovementsSelector);
  const disablePredictionDisplay = useSelector(getHasCriticalMessage);
  const stopFetchingMore = useSelector(hasNoMoreMovements);
  const mode = stopToRouteMode(stop);

  return (
    <FlipMove duration="500" enterAnimation="fade" leaveAnimation="fade" easing="ease-in-out">
      { filteredMovements
        .map((movement) => {
          let key = movement.keyRouteDepartureTime;
          if (movement.vehicleJourneyName) {
            key += `-${movement.vehicleJourneyName}`;
          }
          return (<MovementInfo movement={movement} mode={mode} key={key} disablePredictionDisplay={disablePredictionDisplay} />);
        })
        .concat(!stopFetchingMore && <Spinner key="spinner-id" />) }
    </FlipMove>
  );
};

const LiveDepartureFilterDialog = ({
  displayFilterOptions, onClose, stop,
}) => {
  const filteredRoutesNames = useSelector(({ liveDeparture }) => liveDeparture.filteredRoutesNames);

  return (
    <FilterRouteOptionsDialog
      isOpen={displayFilterOptions}
      onClose={onClose}
      stop={stop}
      key={`${stop.stopCode}-${!isNil(filteredRoutesNames) ? filteredRoutesNames.toString() : 'nil'}`}
    />
  );
};
LiveDepartureFilterDialog.propTypes = {
  displayFilterOptions: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stop: PropTypes.shape({
    stopCode: PropTypes.string,
    stopAddress: PropTypes.string,
  }).isRequired,
};

const logClickEvent = (label) => {
  logEvent(CATEGORIES.LIVE_DEPARTURES, LIVE_DEPARTURES_ACTION.PID_MESSAGE_CLICK, label);
};

const getLinkifiedSegments = (text) => {
  const trimmedText = trim(text, '<> ');
  const matches = linkify.match(trimmedText);
  const result = [];
  let index = 0;
  if (matches) {
    matches.forEach((match) => {
      if (index < match.index) {
        result.push(trimmedText.slice(index, match.index));
      }
      result.push(<a href={match.url} onClick={() => logClickEvent(match.url)}>{match.text}</a>);
      index = match.lastIndex;
    });
    if (index < trimmedText.length) {
      result.push(trimmedText.slice(index));
    }
  } else {
    result.push(trimmedText);
  }
  return result;
};

const LiveDepartureMessage = () => {
  const message = useSelector(getMessage);
  const hasEmergencyMessage = useSelector(getHasEmergencyMessage);
  const hasCriticalMessage = useSelector(getHasCriticalMessage);
  if (!message) {
    return null;
  }

  return (
    <div className="live-departures__message-container">
      <div className={`at-alert ${(hasEmergencyMessage || hasCriticalMessage) ? 'is-urgent' : 'is-warning'}`}>
        <IconAlert className="info-box-icon" role="img" />
        <div className="info-box-content">
          <div className="content-text">
            {
              getLinkifiedSegments(message.text)
                .map(slice => (
                  <React.Fragment key={slice}>{slice}</React.Fragment>
                ))
            }
          </div>
        </div>
      </div>
    </div>
  );
};

const LiveDepartureDisplayBoardContainer = () => {
  const dispatch = useDispatch();
  const hasNoData = useSelector(hasNoDataSelector);
  const hasMovements = useSelector(hasMovementsSelector);
  const hasFilteredMovements = useSelector(hasFilteredMovementsSelector);
  const isRequestingMore = useSelector(isRequestingMoreMovements);
  const stopFetchingMore = useSelector(hasNoMoreMovements);
  const hasError = useSelector(hasErrorSelector);
  const movements = useSelector(getMovements);
  const stop = useSelector(getSelectedStop);
  const displaySurveyButton = useSelector(displaySurveyButtonSelector);
  const displayEmptyContent = useSelector(getHasEmergencyMessage);

  const [displayFilterOptions, setDisplayFilterOptions] = useState(false);
  const [isStopMatchingMovements, setStopMatchingMovements] = useState(false);

  const stopCodeRef = useRef(stop?.stopCode);
  const fetchDataTimeoutId = useRef();
  const listContainerRef = useRef();

  const getDepartures = (shouldRequest, stopCode, options) => {
    dispatch(LiveDepartureActions.getDepartures(shouldRequest, stopCode, options));
  };

  const appendToDepartures = (stopCode, options) => {
    dispatch(LiveDepartureActions.getMoreDepartures(stopCode, options));
  };

  const getFilteredRoutesNames = (stopCode) => {
    dispatch(LiveDepartureActions.getFilteredRoutesNames(stopCode));
  };

  const updateFilteredRoutesNames = (filteredRouteNames) => {
    dispatch(LiveDepartureActions.updateFilteredRoutesNames(filteredRouteNames));
  };

  const updateDisplaySurveyButton = (display) => {
    dispatch(LiveDepartureActions.updateDisplaySurveyButton(display));
  };

  const fetchData = (options) => {
    if (stopCodeRef.current) {
      getDepartures(true, stopCodeRef.current, options);
      fetchDataTimeoutId.current = setTimeout(fetchData, DATA_FETCH_INTERVAL);
    }
  };

  const fetchMoreData = () => {
    const options = {};
    if (movements?.length) {
      options.startDate = moment(movements[movements.length - 1].scheduledDepartureTime).add(1, 'seconds').toISOString();
    }
    appendToDepartures(stopCodeRef.current, options);
  };

  const cancelFetchData = () => {
    if (fetchDataTimeoutId.current) {
      clearTimeout(fetchDataTimeoutId.current);
      fetchDataTimeoutId.current = null;
    }
  };

  // Check if we need to fetch more data if the current data doesn't fill the list
  const fetchMoreDataIfListIsNotFilled = () => {
    if (listContainerRef.current && movements?.length) {
      const isVisibleListFull = listContainerRef.current.scrollHeight > listContainerRef.current.clientHeight;
      if (!stopFetchingMore && !isRequestingMore && !isVisibleListFull && stopCodeRef.current) {
        fetchMoreData();
      }
    }
  };

  useEffect(() => {
    fetchData({ startDate: moment().toISOString() }); // Fetch only the first two hours
    return cancelFetchData;
  }, []);

  useEffect(() => {
    stopCodeRef.current = stop?.stopCode;

    updateFilteredRoutesNames(null);
    cancelFetchData();
    if (stopCodeRef.current) {
      getDepartures(false);

      fetchData({ startDate: moment().toISOString() }); // Fetch only the first two hours
      getFilteredRoutesNames(stopCodeRef.current);
    }
  }, [stop]);

  useEffect(() => {
    fetchMoreDataIfListIsNotFilled();
  }, [movements]);

  useEffect(() => {
    const determineMatchingStop = async (movements, stop) => {
      const firstMovementStopCode = movements?.[0]?.stop_code;
      if (!firstMovementStopCode || !stop || firstMovementStopCode === stop?.stopCode) {
        setStopMatchingMovements(true);
      } else {
        const gtfsStop = await getStopByCode(firstMovementStopCode);
        const parentStop = gtfsStop?.parent_station ? await getStopById(gtfsStop?.parent_station) : null;
        setStopMatchingMovements(parentStop?.stop_code === stop?.stopCode);
      }
    }
    determineMatchingStop(movements, stop);
  }, [movements, stop]);

  const onScrollLiveDeparturesList = (event) => {
    const isBottom = event.target.scrollHeight - event.target.scrollTop - LOADING_SPINNER_HEIGHT <= event.target.clientHeight;
    if (!stopFetchingMore && !isRequestingMore && isBottom && stopCodeRef.current) {
      fetchMoreData();
    }
  };

  const onFilterOptionsClick = () => {
    setDisplayFilterOptions(true);
  };

  const onCloseFilterOptions = () => {
    setDisplayFilterOptions(false);
  };

  const onCloseSurveyButton = () => {
    updateDisplaySurveyButton(false);
  };

  if (!stop?.stopCode) {
    return null;
  }

  let content = null;
  let gridClassName = '';
  let listClassName = '';
  let listContainerClassName = 'col-sm-8 col-xs-10 col-sm-offset-2 col-xs-offset-1';
  if (hasError) {
    content = <LiveDepartureError />;
  } else if (displayEmptyContent) {
    content = (<div className="stop-message" />);
  } else if (hasNoData || !isStopMatchingMovements) {
    content = <LiveDepartureLoading />;
  } else if (!hasMovements) {
    content = <LiveDepartureNoResult />;
  } else if (!hasFilteredMovements) {
    const mode = stopToRouteMode(stop);
    content = <LiveDepartureFiltered mode={mode} />;
    listClassName = 'live-departures__list--filtered';
  } else {
    content = <LiveDepartureList stop={stop} />;
    gridClassName = 'live-departures--results';
    listClassName = 'live-departures__list--results';
    listContainerClassName = 'col-sm-8 col-xs-12 col-sm-offset-2';
  }

  const shouldDisplaySurvey = displaySurveyButton && (!hasNoData || hasError);
  const footerClassName = shouldDisplaySurvey ? '' : 'live-departures__footer--empty';

  return (
    <div className={`live-departures at-flex-grid ${gridClassName}`}>
      <div className="row">
        <div className="col-xs-10 col-sm-6 col-xs-offset-1 col-sm-offset-3 routes-filter">
          <ATbutton
            className="routes-filter__button"
            block
            backgroundColor="dark"
            onClick={onFilterOptionsClick}
          >
            <IconOptions className="routes-filter__icon" role="img" />
            Filter routes
          </ATbutton>
        </div>
      </div>
      <LiveDepartureFilterDialog displayFilterOptions={displayFilterOptions} onClose={onCloseFilterOptions} stop={stop} />
      <div className="row">
        <div className={`live-departures__list-container ${listContainerClassName}`}>
          <LiveDepartureMessage />
          <ul className={`live-departures__list ${listClassName}`} onScroll={onScrollLiveDeparturesList} ref={listContainerRef} onTransitionEnd={fetchMoreDataIfListIsNotFilled}>
            {content}
          </ul>
        </div>
      </div>
      <div className={`live-departures__footer ${footerClassName}`}>
        <SurveyButton
          display={shouldDisplaySurvey}
          onClose={onCloseSurveyButton}
          className="live-departure-survey"
          href={SURVEY_URL}
        />
      </div>
    </div>
  );
};

export default LiveDepartureDisplayBoardContainer;
